import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../context/auth";
import RoleTypes from "../../../enums/role-types";
import { GlobalLoaderContext } from "../../../context/global-loader";
import { SnackbarContext } from "../../../context/snackbar";
import { useHistory } from "react-router-dom";
import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ActionButtonSmall from "../../../components/layout/buttons/action-button-small";
import CompositionBox from "../../administration/feeds-generator/CompositionBox";
import CompositionsAutocomplete from "../../administration/feeds-generator/CompositionsAutocomplete";
import Compositions from "../../../enums/compositions";
import ProfileTypes from "../../../enums/profile-types";
import ApiUri from "../../../api/api-uri";
import API from "../../../api/api";

const ExportSalesReport = () => {
  const { user, userRole } = useContext(AuthContext);
  const { startGlobalLoader, stopGlobalLoader } =
    useContext(GlobalLoaderContext);
  const { initSnackbarSuccess, initSnackbarError } =
    useContext(SnackbarContext);
  const history = useHistory();
  const [showPage, setShowPage] = useState(false);

  const [invoiceType, setInvoiceType] = useState("");
  const [exportType, setExportType] = useState("");
  const [startDate, setStartDate] = useState(null as any);
  const [endDate, setEndDate] = useState(null);

  const handleSeeThisPage = (seeThisPage) => {
    if (!seeThisPage) {
      initSnackbarError("You don't have permission to access this page.");
      history.push("/dashboard");
      return;
    } else {
      setShowPage(true);
    }
  };

  useEffect(() => {
    if (
      user?.last_accessed_profile?.profile_type?.name === ProfileTypes.SERVICE
    ) {
      const seeThisPage =
        userRole === RoleTypes.ADMIN ||
        userRole === RoleTypes.OWNER ||
        user?.is_mmpz_service_admin;
      handleSeeThisPage(seeThisPage);
    } else {
      startGlobalLoader();
      const parentProfileId = user?.last_accessed_profile?.parent?.id;
      API.get(ApiUri.PROFILE + "/" + parentProfileId).then((response) => {
        const parentProfile = response.data;
        const userRoleForParentProfileFromRbac =
          parentProfile?.profile_rbac.find(
            (profile) => profile.user.id === user?.id,
          )?.role.name;
        const userRoleForParentProfile =
          userRoleForParentProfileFromRbac ?? RoleTypes.READ_ONLY;
        const seeThisPage =
          userRole === RoleTypes.ADMIN ||
          userRole === RoleTypes.OWNER ||
          userRoleForParentProfile === RoleTypes.ADMIN ||
          userRoleForParentProfile === RoleTypes.OWNER ||
          user?.is_mmpz_service_admin;
        stopGlobalLoader();
        handleSeeThisPage(seeThisPage);
      });
    }
  }, []);

  const handleExportTypeChange = (event) => {
    const { value } = event.target;
    setExportType(value);
  };

  const handleStartDateChange = (event) => {
    const newDate = event;
    setStartDate(newDate);
  };
  const handleEndDateChange = (event) => {
    const newDate = event.$d;
    setEndDate(newDate);
  };

  const handleInvoiceTypeChange = (event) => {
    const { value } = event.target;
    setInvoiceType(value);
  };

  const formatDate = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handleSave = () => {
    const payload = {
      date_from: startDate ? formatDate(startDate?.$d) : null,
      date_to: endDate ? formatDate(endDate) : null,
      type: invoiceType,
    };
    const route =
      exportType === "CSV" ? ApiUri.DOWNLOAD_CSV : ApiUri.DOWNLOAD_ZIP;
    const url =
      ApiUri.PROFILE +
      "/" +
      user?.last_accessed_profile?.id +
      ApiUri.SALES +
      ApiUri.INVOICES +
      route;

    startGlobalLoader();
    API.get(url, { params: payload })
      .then((response) => {
        initSnackbarSuccess(response.data?.message);
      })
      .catch((error) => {
        initSnackbarError(error);
      })
      .finally(() => {
        stopGlobalLoader();
      });
  };

  const formIsValid = startDate && endDate && exportType && invoiceType;

  return (
    showPage && (
      <Stack marginTop={2} padding={3} spacing={2}>
        <FormControl>
          <InputLabel>Invoice Type</InputLabel>
          <Select
            value={invoiceType}
            onChange={handleInvoiceTypeChange}
            label="Invoice Type"
          >
            <MenuItem value={"All"} key={"All"}>
              All
            </MenuItem>
            <MenuItem value={"SUBSCRIPTION"} key={"SUBSCRIPTION"}>
              Subscriptions
            </MenuItem>
            <MenuItem value={"ROYALTY_PAID"} key={"ROYALTY_PAID"}>
              Royalty Paid
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel>Export Type</InputLabel>
          <Select
            value={exportType}
            onChange={handleExportTypeChange}
            label="Export Type"
          >
            <MenuItem value={"ZIP"} key={"ZIP"}>
              ZIP
            </MenuItem>
            <MenuItem value={"CSV"} key={"CSV"}>
              CSV
            </MenuItem>
          </Select>
        </FormControl>

        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction={"row"} spacing={2}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                format="DD/MM/YYYY"
                disableFuture
                sx={{ width: "100%" }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                format="DD/MM/YYYY"
                disableFuture
                sx={{ width: "100%" }}
                minDate={startDate}
              />
            </Stack>
          </LocalizationProvider>
        </>

        <ActionButtonSmall onClick={handleSave} disabled={!formIsValid}>
          Export
        </ActionButtonSmall>
      </Stack>
    )
  );
};

export default ExportSalesReport;
